import { Collapse, Divider, Stack, Typography, useTheme } from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { FC, JSX } from "react";
import { Children, type PropsWithChildren, useEffect, useState } from "react";

type Props = {
	label: string;
	value?: string;
	adorment?: JSX.Element;
	endAdornment?: JSX.Element;
	defaultExpanded?: boolean;
	expanded?: boolean;
	onToggle?: (expanded: boolean) => void;
	noDivider?: boolean;
};

export const DataRowCollapsible: FC<PropsWithChildren<Props>> = ({
	label,
	value,
	adorment,
	endAdornment,
	defaultExpanded = false,
	expanded: expandedProp,
	onToggle,
	children,
	noDivider = false,
}) => {
	const theme = useTheme();
	const hasContent = Children.toArray(children).length > 0;
	const isControlled = expandedProp !== undefined;
	const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
	const expanded = isControlled ? expandedProp : internalExpanded;

	useEffect(() => {
		if (isControlled) return;
		setInternalExpanded(defaultExpanded);
	}, [defaultExpanded, isControlled]);

	const toggle = () => {
		const next = !expanded;
		if (onToggle) onToggle(next);
		if (!isControlled) setInternalExpanded(next);
	};

	return (
		<Stack>
			<Stack
				onClick={hasContent ? toggle : undefined}
				direction="row"
				alignItems="center"
				justifyContent="space-between"
			>
				<Stack direction="row" alignItems="center" gap={0.8}>
					<Typography
						variant="deprecatedBodyStrong"
						color={theme.palette.deprecated.grey[700]}
					>
						{label}
					</Typography>
					{adorment}
				</Stack>
				<Stack direction="row" alignItems="center" gap={0.4}>
					<Typography
						variant="deprecatedBodyStrong"
						color={theme.palette.deprecated.grey[700]}
					>
						{value}
					</Typography>
					{hasContent &&
						(expanded ? (
							<ChevronUp size={16} color={theme.palette.primary.main} />
						) : (
							<ChevronDown size={16} color={theme.palette.primary.main} />
						))}
				</Stack>
			</Stack>
			{endAdornment}
			{hasContent && (
				<Collapse in={expanded} timeout="auto" unmountOnExit>
					<Stack pt={0.8}>{children}</Stack>
				</Collapse>
			)}
			{!noDivider && (
				<Stack py={1.2}>
					<Divider sx={{ borderColor: theme.palette.deprecated.grey[100] }} />
				</Stack>
			)}
		</Stack>
	);
};
